<template>
  <div
    id="MissionDetailMap"
    :style="{ height }"
    class="mission-detail-map flex-fixed"
  >
    <div
      ref="container"
      :style="{ height }"
      class="mission-detail-map__canvas"
    />
  </div>
</template>
<script>
  import { mapActions, mapGetters } from 'vuex'

  import {
    BASE_MAP_OPTIONS,
    FLY_DURATION,
    addZoomControl,
    bindPopup,
    fitToPositions,
    icons,
    loadGoogleMaps,
    popupAnchorPoint
  } from '@/services/GoogleMaps'

  export default {
    name: 'MissionDetailMap',
    props: {
      mission: {
        type: Object,
        default: null
      },
      height: {
        type: String,
        required: true
      },
      directionFocus: {
        type: String,
        default: null
      }
    },
    data () {
      return {
        mapData: {
          zoom: 4,
          maxZoom: 14,
          minZoom: 2,
          center: { lat: 48.8664982, lng: 2.3348235 }
        },
        itinerary: {
          timeout: null,
          visible: false,
          path: null,
          style: {
            color: null, // This will be set dynamically in mounted()
            opacity: 1,
            weight: 3
          }
        }
      }
    },
    computed: {
      ...mapGetters('missions', [
        'getMissionsItineraries',
        'getCurrentMission'
      ]),
      getItineraryColor () {
        // The colors are defined in _colors.scss
        return getComputedStyle(document.documentElement)
          .getPropertyValue('--info')
      },
      /**
       * Returns the padding kept around whatever the camera frames.
       * @function flyPadding
       */
      flyPadding () {
        return { top: 32, right: 32, bottom: 32, left: 32 }
      },
      /**
       * @function isInformationTab
       */
      isInformationTab () {
        return this.directionFocus === 'travel'
      },
      /**
       * @function isPickupCompleted
       */
      isPickupCompleted () {
        return this.mission.pickup.state === 'completed'
      },
      /**
       * @function isDeliveryCompleted
       */
      isDeliveryCompleted () {
        return this.mission.delivery.state === 'completed'
      }
    },
    watch: {
      mission: {
        handler () {
          this.showMission()
        },
        deep: true
      },
      directionFocus () {
        this.showMission()
      }
    },
    created () {
      /**
       * Everything drawn on the map, kept so it can be taken off again. Set here
       * rather than in `data`: Vue has no business walking Google objects.
       */
      this.map = null
      this.markers = {}
      this.popups = []
      this.route = null
    },
    async mounted () {
      this.itinerary.style.color = this.getItineraryColor

      const maps = await loadGoogleMaps()
      if (!this.$refs.container) return

      this.map = new maps.Map(this.$refs.container, {
        ...BASE_MAP_OPTIONS,
        zoom: this.mapData.zoom,
        maxZoom: this.mapData.maxZoom,
        minZoom: this.mapData.minZoom,
        center: this.mapData.center,
        scrollwheel: false
      })

      addZoomControl(this.map, 'RIGHT_TOP').classList.add('ctk-map-zoom--compact')

      /**
       * The map is built while its panel is still being laid out, and Google
       * sizes itself once: without this it draws into the size the container
       * had then.
       */
      setTimeout(() => {
        if (!this.map) return

        maps.event.trigger(this.map, 'resize')
        this.showMission()
      }, 300)
    },
    beforeDestroy () {
      this.removeMarkers()
      this.removeLine()
      this.map = null
    },
    methods: {
      ...mapActions('missions', ['getMissionTravel', 'pushMissionItinerary']),
      /**
       * Drops the markers afresh, which also closes any popup left open, then
       * fetches the itinerary and frames the camera.
       * @function showMission
       */
      showMission () {
        if (!this.map || !this.mission) return

        this.drawMarkers()
        this.retrieveItinerary(this.mission)
        this.$nextTick(() => {
          this.zoomToMarkers()
        })
      },
      /**
       * @function drawMarkers
       */
      drawMarkers () {
        this.removeMarkers()

        const directions = ['pickup', 'delivery']
        directions.forEach(direction => {
          const { address } = this.mission[direction]
          const marker = new window.google.maps.Marker({
            map: this.map,
            position: address.location,
            icon: icons[direction]('in_progress'),
            anchorPoint: popupAnchorPoint()
          })

          this.popups.push(bindPopup(this.map, marker, this.formatPopup(address, direction)))
          this.markers[direction] = marker
        })
      },
      /**
       * @function removeMarkers
       */
      removeMarkers () {
        this.popups.forEach(popup => popup.close())
        Object.values(this.markers).forEach(marker => marker.setMap(null))
        this.popups = []
        this.markers = {}
      },
      formatPopup (address, direction) {
        const comment = `
          <div class="content tw-text-gray-700">
            ${address.comment}
          </div>
        `
        return `
          <div class="header ${direction}">
            <b>${address.name}</b><br>
            ${address.street_name} - ${address.postal_code}<br>
            ${address.city}, ${this.$t(address.country)}
          </div>
          ${address.comment ? comment : ''}
        `
      },
      /**
       * Shows the itinerary once the camera has settled, as a line redrawn at
       * every animation frame reads as flicker.
       * @function showItineraryAfterFlight
       */
      showItineraryAfterFlight () {
        this.itinerary.timeout = setTimeout(() => {
          this.itinerary.visible = true
          if (this.route) this.route.setVisible(true)
        }, FLY_DURATION * 1000)
      },
      /**
       * Zoom to a single marker, and open its popup.
       * @function zoomToMarker
       * @param {string} direction
       */
      zoomToMarker (direction) {
        const marker = this.markers[direction]
        if (!this.map || !marker) return

        this.map.panTo(marker.getPosition())
        this.map.setZoom(10)

        this.showItineraryAfterFlight()

        setTimeout(() => {
          window.google.maps.event.trigger(marker, 'click')
        }, 900)
      },
      zoomToMarkers () {
        if (
          this.isInformationTab ||
          (!this.isInformationTab && this.isPickupCompleted && this.isDeliveryCompleted)
        ) {
          /**
           * Zoom to the marker group
           */
          const markers = Object.values(this.markers)
          if (!this.map || !markers.length) return

          try {
            fitToPositions(this.map, markers.map(marker => marker.getPosition()), {
              padding: this.flyPadding
            })

            this.showItineraryAfterFlight()
          } catch (e) {
            console.error('Could not zoom to the marker.', e)
          }
        } else if (!this.isPickupCompleted) {
          this.zoomToMarker('pickup')
        } else {
          this.zoomToMarker('delivery')
        }
      },
      /**
       * Draws the itinerary line, hidden until the camera has settled.
       * @function drawItinerary
       * @param {Array<[number, number]>} coordinates - `[lng, lat]` pairs
       */
      drawItinerary (coordinates) {
        this.itinerary.path = coordinates
          .map(location => ({
            lat: location[1],
            lng: location[0]
          }))

        if (this.route) this.route.setMap(null)
        if (!this.map) return

        this.route = new window.google.maps.Polyline({
          map: this.map,
          path: this.itinerary.path,
          strokeColor: this.itinerary.style.color,
          strokeOpacity: this.itinerary.style.opacity,
          strokeWeight: this.itinerary.style.weight,
          visible: this.itinerary.visible
        })
      },
      /**
       * Remove the line itinerary from the map
       * @function removeLine
       */
      removeLine () {
        this.itinerary.visible = false
        this.itinerary.path = null
        if (this.route) {
          this.route.setMap(null)
          this.route = null
        }
        if (this.itinerary.timeout) {
          clearTimeout(this.itinerary.timeout)
          this.itinerary.timeout = null
        }
      },
      retrieveItinerary (mission) {
        this.removeLine()

        const exists = this.getMissionsItineraries.find(e => e.uuid === mission.uuid)
        if (exists) {
          this.drawItinerary(exists.itinerary.coordinates)
        } else {
          this.getMissionTravel()
            .then((res) => {
              if (res.data.itinerary) {
                this.pushMissionItinerary({
                  uuid: mission.uuid,
                  itinerary: res.data.itinerary
                })

                /**
                 * Force the itinerary change only if the offer didn't change during
                 * the request.
                 */
                if (this.getCurrentMission.uuid === mission.uuid) {
                  this.drawItinerary(res.data.itinerary.coordinates)
                }
              }
            })
            .catch((err) => {
              console.log('Error occured while retrieving this itinerary', err)
            })
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .mission-detail-map {
    position: relative;

    &__canvas {
      background: #EAEAEA;
      width: 100%;
      height: 100%;
      min-height: 200px;
      z-index: 0;
    }
  }
</style>
